import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Modal, Row } from 'react-bootstrap';

import DescriptionPanel from './DescriptionPanel';
import TwoColumnGrid from './TwoColumnGrid';

/**
 * Modal de solo visualización de un gasto.
 * Diseño profesional: tarjeta izquierda con descripción multilínea y metadatos,
 * tarjeta derecha con montos destacados y badges. Botón de descarga visible.
 */
const ModalVisualizarDetallesGasto = ({ show, selectedGasto, onClose }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [downloading, setDownloading] = useState(false);

    // normalize: sometimes selectedGasto might be an array
    const gasto = Array.isArray(selectedGasto) ? selectedGasto[0] : (selectedGasto || {});

    useEffect(() => {
        // noop
    }, [gasto]);

    const formatDate = (value) => {
        try {
            if (!value) return "";
            const d = new Date(value);
            return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return value;
        }
    };

    const formatCurrency = (value, currency) => {
        if (value == null || value === "") return "";
        try {
            const num = typeof value === 'number' ? value : parseFloat(value);
            if (Number.isNaN(num)) return "";
            return num.toLocaleString('es-MX', { style: 'currency', currency: currency || gasto?.vMoneda || 'MXN' });
        } catch {
            return value;
        }
    };

    const descargarNotaDesdeApi = async (idGasto, setDownloadingLocal) => {
        setDownloadingLocal(true);
        try {
            const response = await fetch(`/api/Gastos/ObtenerNotaVenta?idGasto=${idGasto}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const detalles = JSON.parse(data.detalles);
                    const { vNotaVenta_Base64, vNotaVenta_Name } = detalles.table2[0];

                    const byteCharacters = atob(vNotaVenta_Base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/octet-stream' });

                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = vNotaVenta_Name;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(link.href);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener nota de venta');
                }
            } else {
                showErrorToast('Error en la petición: ' + response.statusText);
            }
        } catch (error) {
            showErrorToast('Error al descargar nota de venta');
            console.error('Error al descargar nota de venta:', error);
        } finally {
            setDownloadingLocal(false);
        }
    };

    const rightItems = [
        { label: 'Cantidad', value: gasto?.dCantidad },
        { label: 'Precio unitario', value: formatCurrency(gasto?.dPrecioUnitario) },
        { label: 'Descuento', value: formatCurrency(gasto?.dDescuento), raw: gasto?.dDescuento, highlight: true },
        { label: 'Importe', value: formatCurrency(gasto?.dImporte) },
        { label: 'IVA', value: formatCurrency(gasto?.dIva) },
        { label: 'IVA Retenido', value: formatCurrency(gasto?.dIvaRet), raw: gasto?.dIvaRet, highlight: true },
        { label: 'ISR Retenido', value: formatCurrency(gasto?.dIsrRet), raw: gasto?.dIsrRet, highlight: true },
        { label: 'Tipo de cambio', value: gasto?.dTipoCambio }
    ];

    // compute converted total to MXN when currency != MXN
    const computeTotalInMXN = () => {
        try {
            const total = gasto?.dTotal != null ? parseFloat(gasto.dTotal) : null;
            if (total == null || Number.isNaN(total)) return null;
            const rate = gasto?.dTipoCambio != null ? parseFloat(gasto.dTipoCambio) : 1;
            if (Number.isNaN(rate) || rate <= 0) return null;
            // Assume dTotal is in gasto.vMoneda, convert to MXN by multiplying by rate
            return total * rate;
        } catch {
            return null;
        }
    };

    const convertedTotalMXN = gasto?.vMoneda && gasto.vMoneda !== 'MXN' ? computeTotalInMXN() : null;

    return (
        <Modal show={show} centered size="lg" onHide={onClose} backdrop="static" keyboard={false}>
            <Modal.Header className="border-0">
                <div>
                    <Modal.Title className="d-flex align-items-center gap-2">
                        Detalles del Gasto
                        {/* <div className="text-muted small">{gasto?.vDescripcion ? null : 'Sin descripción'}</div> */}
                    </Modal.Title>
                </div>
                <Button className="btn-close" onClick={onClose} />
            </Modal.Header>

            <Modal.Body>
                <Row>
                    <Col lg={7} className="mb-3">
                        <DescriptionPanel gasto={gasto} onDownload={() => descargarNotaDesdeApi(gasto.idGasto, setDownloading)} downloading={downloading} />
                    </Col>

                    <Col lg={5} className="mb-3">
                        <Card className="h-100 shadow-sm">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-baseline mb-3">
                                    <h6 className="mb-0">Montos</h6>
                                    <div className="text-muted small">Moneda: {gasto?.vMoneda || 'MXN'}</div>
                                </div>

                                <TwoColumnGrid items={rightItems} />

                                <hr />
                                <div className="text-end">
                                    <div className="d-block small text-muted">Creado: {formatDate(gasto?.dFechaRegistro || gasto?.dFechaGasto)}</div>
                                    <div className="d-block small text-muted">Última actualización: {formatDate(gasto?.dFechaMod || gasto?.dFechaActualizacion)}</div>
                                </div>

                                <div className="mt-3 text-center">
                                    <div className="fw-bold fs-4">{formatCurrency(gasto?.dTotal)}</div>
                                    <div className="small text-muted">Total</div>
                                    {convertedTotalMXN != null && (
                                        <div className="mt-2 text-primary ">Equivalente: {formatCurrency(convertedTotalMXN, 'MXN')}</div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {gasto?.detallesExtra && (
                    <Row className="mt-3">
                        <Col>
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <Card.Title>Detalles adicionales</Card.Title>
                                    <pre className="small" style={{ whiteSpace: 'pre-wrap' }}>{gasto.detallesExtra}</pre>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalVisualizarDetallesGasto.propTypes = {
    show: PropTypes.bool.isRequired,
    selectedGasto: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    onClose: PropTypes.func.isRequired
};

export default ModalVisualizarDetallesGasto;
